// 로그인/비로그인 사용자에게 동일한 카트 인터페이스를 제공하는 훅.
//  - 로그인: 서버 카트(/cart) 와 React Query 로 동기화.
//  - 비로그인: 로컬 zustand 카트 + 서버에서 상품 정보를 별도 조회해 UI 표시용 CartViewDto 를 합성.
//
// 의도:
//  - CartPage 와 ProductCard/ProductDetail 의 "담기" 버튼이 본 훅 하나만 호출하도록 한다.
//  - 비로그인 동안 가격이 바뀌었거나 품절된 경우에도 항상 서버의 현재 상품 정보를 기준으로 표시한다.

import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { CART_ITEM_QUANTITY_MAX, lineExpectedGramsG, lineExpectedPriceKrw } from "@oo/shared";
import type {
  CartItemDto,
  CartItemOptionDto,
  CartItemOptionInput,
  CartViewDto,
  ProductDto,
  ProductUnit,
} from "@oo/shared";
import { useAuthStore } from "@/stores/authStore.ts";
import { useGuestCartStore } from "@/stores/cartStore.ts";
import {
  addCartItem as apiAddCartItem,
  getCart as apiGetCart,
  mergeGuestCart as apiMergeGuestCart,
  removeCartItem as apiRemoveCartItem,
  updateCartItemQuantity as apiUpdateCartItemQuantity,
} from "./cartApi.ts";
import { getProduct } from "@/features/catalog/catalogApi.ts";

const CART_QUERY_KEY = ["cart", "view"] as const;

// 비로그인 카트: 상품 정보 맵만 조회한다(키는 productId 집합=멤버십). 수량은 스토어에서 즉시 합성하므로
// 수량을 바꿔도 이 조회는 다시 돌지 않는다(예전엔 수량까지 키에 넣어 변경 시 재조회되며 옛 수량이 잠깐 보였다).
async function loadGuestProducts(
  productIds: ReadonlyArray<string>,
): Promise<Record<string, ProductDto | null>> {
  const map: Record<string, ProductDto | null> = {};
  for (const pid of productIds) {
    try {
      map[pid] = await getProduct(pid);
    } catch {
      // 비공개/삭제된 상품 -> NOT_FOUND. unavailable 로 표시.
      map[pid] = null;
    }
  }
  return map;
}

// 스토어 수량 + 상품 정보 맵으로 CartViewDto 를 동기 합성한다(수량 변경이 즉시 반영, 깜빡임 없음).
function buildGuestView(
  items: ReadonlyArray<{
    productId: string;
    quantity: number;
    options?: ReadonlyArray<{ optionId: string; quantity: number }>;
    addedAt: string;
  }>,
  products: Record<string, ProductDto | null>,
): CartViewDto {
  const cartItems: CartItemDto[] = [];
  let totalKrw = 0;
  let unavailableCount = 0;
  let soldOutCount = 0;
  for (const it of items) {
    const product = products[it.productId] ?? null;
    const isAvailable = product !== null;
    const isSoldOut = product?.isSoldOut === true;
    // 저장된 옵션 id 를 상품의 현재 옵션 마스터와 맞춰 표시값을 만든다(그 사이 삭제된 옵션은 버린다).
    const optionDtos: CartItemOptionDto[] = [];
    for (const sel of it.options ?? []) {
      const master = product?.options.find((o) => o.id === sel.optionId);
      if (master) {
        optionDtos.push({
          optionId: master.id,
          label: master.label,
          addGrams: master.addGrams,
          addPriceKrw: master.addPriceKrw,
          quantity: sel.quantity,
        });
      }
    }
    // 팩 상품은 1팩 중량을 기준중량으로 쓴다(단위 기준중량은 게스트 합성 경로에서 알 수 없음).
    const baseGramsG = product?.packWeightG ?? null;
    const totalG = lineExpectedGramsG(baseGramsG, it.quantity, optionDtos);
    const lineTotal =
      isAvailable && !isSoldOut
        ? lineExpectedPriceKrw(product?.priceKrw ?? 0, it.quantity, optionDtos)
        : 0;
    cartItems.push({
      // 비로그인 카트는 서버 행 ID 가 없으므로 productId 를 임시 ID 로 둔다.
      id: `guest:${it.productId}`,
      productId: it.productId,
      quantity: it.quantity,
      productSnapshot: {
        name: product?.name ?? "(삭제된 상품)",
        priceKrw: product?.priceKrw ?? 0,
        unit: (product?.unit ?? "each") as ProductUnit,
        mainImageKey: product?.mainImageKey ?? null,
        isSoldOut,
        isAvailable,
        baseGramsG,
        weightAdjustable: product?.weightAdjustable === true,
      },
      options: optionDtos,
      lineTotalKrw: lineTotal,
      totalWeightG: totalG > 0 ? totalG : null,
      createdAt: it.addedAt,
      updatedAt: it.addedAt,
    });
    totalKrw += lineTotal;
    if (!isAvailable) {
      unavailableCount += 1;
    } else if (isSoldOut) {
      soldOutCount += 1;
    }
  }
  return { items: cartItems, totalKrw, itemCount: cartItems.length, unavailableCount, soldOutCount };
}

// 항목 목록에서 합계/카운트를 다시 계산해 CartViewDto 를 만든다(낙관적 업데이트용).
function buildCartView(items: CartItemDto[]): CartViewDto {
  let totalKrw = 0;
  let unavailableCount = 0;
  let soldOutCount = 0;
  for (const it of items) {
    totalKrw += it.lineTotalKrw;
    if (!it.productSnapshot.isAvailable) {
      unavailableCount += 1;
    } else if (it.productSnapshot.isSoldOut) {
      soldOutCount += 1;
    }
  }
  return { items, totalKrw, itemCount: items.length, unavailableCount, soldOutCount };
}

// 한 항목의 수량을 반영해 라인 합계를 다시 계산한다(사용 불가/품절이면 0).
function withQuantity(it: CartItemDto, quantity: number): CartItemDto {
  const s = it.productSnapshot;
  // 추가 옵션 금액/중량은 기본 수량과 무관하게 유지된다(옵션은 자체 개수를 가진다).
  const lineTotalKrw =
    s.isAvailable && !s.isSoldOut ? lineExpectedPriceKrw(s.priceKrw, quantity, it.options) : 0;
  const totalG = lineExpectedGramsG(s.baseGramsG, quantity, it.options);
  return { ...it, quantity, lineTotalKrw, totalWeightG: totalG > 0 ? totalG : null };
}

// 카트 합성 결과 + 조작 함수.
export interface UseCartResult {
  view: CartViewDto | undefined;
  isLoading: boolean;
  isLoggedIn: boolean;
  // product 를 함께 넘기면 로그인 사용자도 화면을 먼저 갱신(낙관적)하고 서버 응답으로 동기화한다.
  //  - options: 담기 모달에서 고른 추가 옵션(선택). 서버가 상품 소유 여부를 재검증한다.
  addItem: (
    productId: string,
    quantity: number,
    product?: ProductDto,
    options?: CartItemOptionInput[],
  ) => Promise<{ ok: true; capped: boolean } | { ok: false; reason: "distinct-limit" | "sold-out" | "not-found" }>;
  // 수량을 delta(+1/-1) 만큼 조절한다. 화면은 즉시 반영하고 서버 동기화는 디바운스한다(연타 대응).
  changeQuantity: (item: { id: string; productId: string }, delta: number) => void;
  removeItem: (item: { id: string; productId: string }) => void;
  // 메인/탭바에서 배지로 보여줄 항목 수.
  count: number;
}

// 카트 상태/조작 훅. 로그인 시 서버 카트, 비로그인 시 로컬 카트를 다룬다.
export function useCart(): UseCartResult {
  const session = useAuthStore((s) => s.session);
  const isLoggedIn = !!session;
  const guestItems = useGuestCartStore((s) => s.items);
  const guestAdd = useGuestCartStore((s) => s.addItem);
  const guestSet = useGuestCartStore((s) => s.setQuantity);
  const guestRemove = useGuestCartStore((s) => s.removeItem);

  const queryClient = useQueryClient();

  // 진행 중인 서버 카트 쓰기 수. 모든 쓰기가 끝나면(0) 권위 카트를 1회 재조회해 정합을 맞춘다.
  //  - 연타/빠른 삭제 시 각 응답을 그대로 덮어쓰면 경쟁으로 화면이 튀므로, 중간 응답은 적용하지 않고
  //    마지막에 한 번만 재조회한다. 화면은 그동안 낙관적 캐시로 즉시 반응한다.
  const inflightRef = useRef(0);
  // 수량 조절 서버 동기화 디바운스 타이머(항목별).
  const qtyTimersRef = useRef<Map<string, number>>(new Map());

  // 로그인 사용자 카트 조회. 비로그인일 때는 비활성.
  const serverQuery = useQuery({
    queryKey: CART_QUERY_KEY,
    queryFn: apiGetCart,
    enabled: isLoggedIn,
    staleTime: 30_000,
  });

  // 비로그인 카트: 상품 정보만 조회(키=productId 집합). 수량 변경은 키를 바꾸지 않아 재조회가 없다.
  const guestProductIds = useMemo(
    () => [...new Set(guestItems.map((it) => it.productId))].sort(),
    [guestItems],
  );
  const guestProductsQuery = useQuery({
    queryKey: ["cart", "guest-products", guestProductIds.join("|")],
    queryFn: () => loadGuestProducts(guestProductIds),
    enabled: !isLoggedIn && guestItems.length > 0,
    staleTime: 30_000,
    // 멤버십(상품 추가/삭제) 변경 시 잠깐의 재조회 동안 이전 맵을 유지해 목록이 끊기지 않게.
    placeholderData: keepPreviousData,
  });
  // 표시용 카트 뷰는 스토어 수량 + 상품 맵으로 즉시 합성(수량 변경이 곧바로 반영).
  const guestView = useMemo<CartViewDto | undefined>(() => {
    if (isLoggedIn) {
      return undefined;
    }
    if (guestItems.length === 0) {
      return { items: [], totalKrw: 0, itemCount: 0, unavailableCount: 0, soldOutCount: 0 };
    }
    const products = guestProductsQuery.data;
    if (!products) {
      return undefined;
    }
    return buildGuestView(guestItems, products);
  }, [isLoggedIn, guestItems, guestProductsQuery.data]);

  // 로그인 직후 1회 머지. session 이 null -> 있음으로 전환된 시점에 실행하고,
  // 머지 성공 후 로컬 카트를 비우고 서버 카트를 다시 가져온다.
  const mergedRef = useRef(false);
  useEffect(() => {
    if (!isLoggedIn) {
      mergedRef.current = false;
      return;
    }
    if (mergedRef.current) {
      return;
    }
    mergedRef.current = true;
    const currentGuestItems = useGuestCartStore.getState().items;
    if (currentGuestItems.length === 0) {
      return;
    }
    (async () => {
      try {
        const merged = await apiMergeGuestCart(
          currentGuestItems.map((it) => ({
            productId: it.productId,
            quantity: it.quantity,
            // 비로그인 때 고른 추가 옵션도 함께 옮긴다.
            ...(it.options && it.options.length > 0 ? { options: it.options } : {}),
          })),
        );
        useGuestCartStore.getState().clear();
        queryClient.setQueryData(CART_QUERY_KEY, merged);
      } catch {
        // 머지 실패 시에도 사용자 흐름을 막지 않는다. 다음 로그인 진입에서 자동 재시도되도록 ref 만 풀어둔다.
        mergedRef.current = false;
      }
    })();
  }, [isLoggedIn, queryClient]);

  // 현재 서버 카트 캐시.
  function snapshotServerCart(): CartViewDto | undefined {
    return queryClient.getQueryData<CartViewDto>(CART_QUERY_KEY);
  }

  // 서버 카트 쓰기를 백그라운드로 실행하고, 모든 쓰기가 끝나면 권위 카트를 1회 재조회한다.
  //  - 중간 응답은 캐시에 적용하지 않는다(연타/동시 삭제 경쟁으로 화면이 튀는 것을 막기 위함).
  //  - 화면은 호출 측의 낙관적 캐시 갱신으로 이미 즉시 반응한 상태다.
  function fireCartWrite(call: () => Promise<unknown>): void {
    inflightRef.current += 1;
    void (async () => {
      try {
        await call();
      } catch {
        // 개별 실패는 무시한다. 마지막 재조회가 서버 기준으로 화면을 정정한다.
      } finally {
        inflightRef.current -= 1;
        if (inflightRef.current === 0) {
          void queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });
        }
      }
    })();
  }

  // 로그인/비로그인 공통 인터페이스로 노출.
  async function addItem(
    productId: string,
    quantity: number,
    product?: ProductDto,
    options?: CartItemOptionInput[],
  ) {
    if (!isLoggedIn) {
      const result = guestAdd(productId, quantity, options);
      return result;
    }
    const prev = snapshotServerCart();
    // product 가 있으면 화면을 먼저 갱신한다(기존 항목이면 수량 증가, 없으면 새 항목 추가).
    //  - 추가 옵션을 함께 담는 경우엔 금액/중량 합산이 복잡하고 서버가 곧 정확한 값을 주므로 낙관적 갱신을 건너뛴다.
    if (prev && product && (!options || options.length === 0)) {
      const items = [...prev.items];
      const idx = items.findIndex((it) => it.productId === productId);
      if (idx >= 0) {
        const cur = items[idx]!;
        items[idx] = withQuantity(cur, Math.min(CART_ITEM_QUANTITY_MAX, cur.quantity + quantity));
      } else {
        items.push({
          id: `optimistic:${productId}`,
          productId,
          quantity,
          productSnapshot: {
            name: product.name,
            priceKrw: product.priceKrw,
            unit: product.unit as ProductUnit,
            mainImageKey: product.mainImageKey,
            isSoldOut: product.isSoldOut,
            isAvailable: true,
            baseGramsG: null,
            weightAdjustable: product.weightAdjustable,
          },
          options: [],
          lineTotalKrw: product.isSoldOut ? 0 : product.priceKrw * quantity,
          totalWeightG: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
      queryClient.setQueryData(CART_QUERY_KEY, buildCartView(items));
    }
    try {
      const cart = await apiAddCartItem(productId, quantity, options);
      queryClient.setQueryData(CART_QUERY_KEY, cart);
      return { ok: true as const, capped: false };
    } catch (err) {
      // 실패 시 낙관적 변경을 되돌린다.
      if (prev) {
        queryClient.setQueryData(CART_QUERY_KEY, prev);
      }
      const code = (err as { code?: string }).code;
      if (code === "CONFLICT") {
        return { ok: false as const, reason: "sold-out" as const };
      }
      if (code === "NOT_FOUND") {
        return { ok: false as const, reason: "not-found" as const };
      }
      throw err;
    }
  }

  // 수량을 delta 만큼 조절한다. 화면(캐시)은 즉시 반영, 서버 동기화는 항목별 디바운스(연타 1회 전송).
  function changeQuantity(item: { id: string; productId: string }, delta: number): void {
    if (!isLoggedIn) {
      const found = useGuestCartStore.getState().items.find((i) => i.productId === item.productId);
      const next = Math.min(CART_ITEM_QUANTITY_MAX, Math.max(1, (found?.quantity ?? 1) + delta));
      guestSet(item.productId, next);
      return;
    }
    // 현재 캐시에서 수량을 읽어 누적한다(연타 시에도 캐시는 동기 갱신되므로 정확히 쌓인다).
    const cur = snapshotServerCart();
    const found = cur?.items.find((it) => it.id === item.id);
    if (!cur || !found) {
      return;
    }
    const nextQty = Math.min(CART_ITEM_QUANTITY_MAX, Math.max(1, found.quantity + delta));
    if (nextQty === found.quantity) {
      return;
    }
    queryClient.setQueryData(
      CART_QUERY_KEY,
      buildCartView(cur.items.map((it) => (it.id === item.id ? withQuantity(it, nextQty) : it))),
    );
    // 디바운스 서버 동기화(마지막 수량만 전송).
    const timers = qtyTimersRef.current;
    const existing = timers.get(item.id);
    if (existing) {
      window.clearTimeout(existing);
    }
    const t = window.setTimeout(() => {
      timers.delete(item.id);
      fireCartWrite(() => apiUpdateCartItemQuantity(item.id, nextQty));
    }, 350);
    timers.set(item.id, t);
  }

  // 항목 삭제. 화면은 즉시 제거하고 서버 삭제는 백그라운드로 보낸다(빠른 연속 삭제 경쟁 방지).
  function removeItem(item: { id: string; productId: string }): void {
    if (!isLoggedIn) {
      guestRemove(item.productId);
      return;
    }
    // 대기 중이던 이 항목의 수량 동기화는 취소(이미 삭제하므로 무의미).
    const pendingTimer = qtyTimersRef.current.get(item.id);
    if (pendingTimer) {
      window.clearTimeout(pendingTimer);
      qtyTimersRef.current.delete(item.id);
    }
    const cur = snapshotServerCart();
    if (cur) {
      queryClient.setQueryData(
        CART_QUERY_KEY,
        buildCartView(cur.items.filter((it) => it.id !== item.id)),
      );
    }
    fireCartWrite(() => apiRemoveCartItem(item.id));
  }

  const view = isLoggedIn ? serverQuery.data : guestView;
  const isLoading = isLoggedIn
    ? serverQuery.isLoading
    : guestItems.length > 0 && !guestProductsQuery.data;
  const count = view?.itemCount ?? (isLoggedIn ? 0 : guestItems.length);

  return {
    view,
    isLoading,
    isLoggedIn,
    addItem,
    changeQuantity,
    removeItem,
    count,
  };
}

// 상단바/탭바 배지용 경량 훅 - 항목 수만 빠르게 반환한다.
//  - 비로그인이면 zustand 의 길이를 그대로.
//  - 로그인이면 서버 카트 캐시(CartViewDto.itemCount) 가 있으면 사용하고, 없으면 0.
export function useCartItemCount(): number {
  const session = useAuthStore((s) => s.session);
  const guestCount = useGuestCartStore((s) => s.items.length);
  const queryClient = useQueryClient();
  if (!session) {
    return guestCount;
  }
  const cached = queryClient.getQueryData<CartViewDto>(CART_QUERY_KEY);
  return cached?.itemCount ?? 0;
}
